import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(amount);
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

const MarginCalculator = () => {
  // State for basic inputs
  const [availableCapital, setAvailableCapital] = useState(100000);
  const [segment, setSegment] = useState('equity-delivery');
  const [marginType, setMarginType] = useState('intraday');
  
  // State for segment-specific inputs
  const [leverage, setLeverage] = useState(5);
  const [exposureMargin, setExposureMargin] = useState(2);
  const [spanMargin, setSpanMargin] = useState(15);
  const [stockCategory, setStockCategory] = useState('group-a');
  
  // State for advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [volatilityMargin, setVolatilityMargin] = useState(3);
  const [adhocMargin, setAdhocMargin] = useState(0);
  const [additionalMargin, setAdditionalMargin] = useState(0);
  const [maintenanceMargin, setMaintenanceMargin] = useState(50);
  
  // State for share toast
  const [showShareToast, setShowShareToast] = useState(false);
  
  // State for tooltips
  const [showToolTip, setShowToolTip] = useState('');
  
  // State for calculation results
  const [result, setResult] = useState({
    marginRequired: 0,
    totalExposure: 0,
    effectiveLeverage: 0,
    marginUtilization: 0,
    maxPositionsNumber: 0,
    initialMarginRequired: 0,
    maintenanceMarginRequired: 0,
    marginCallThreshold: 0,
    minAvailableMargin: 0
  });

  // Calculate margin value and related metrics
  const calculateMarginValue = () => {
    let marginRequired = 0;
    let totalExposure = 0;
    let effectiveLeverage = 1;
    let marginUtilization = 0;
    let maxPositionsNumber = 0;
    let initialMarginRequired = 0;
    let maintenanceMarginRequired = 0;
    let marginCallThreshold = 0;
    let minAvailableMargin = 0;

    // Calculate based on segment and margin type
    if (segment === 'equity-delivery') {
      if (marginType === 'delivery') {
        // For equity delivery
        marginRequired = availableCapital; // 100% margin required
        totalExposure = availableCapital;
        effectiveLeverage = 1;
      } else {
        // For equity intraday with delivery segment
        marginRequired = availableCapital / leverage;
        totalExposure = availableCapital * leverage;
        effectiveLeverage = leverage;
      }
    } else if (segment === 'equity-intraday') {
      // Margin requirement based on stock category
      let marginRequirement = 0;
      if (stockCategory === 'group-a') {
        marginRequirement = 0.2; // 20% for Group A stocks
      } else if (stockCategory === 'group-b') {
        marginRequirement = 0.3; // 30% for Group B stocks
      } else {
        marginRequirement = 0.35; // 35% for other stocks
      }
      
      marginRequired = availableCapital * marginRequirement;
      totalExposure = availableCapital / marginRequirement;
      effectiveLeverage = 1 / marginRequirement;
    } else if (segment === 'futures') {
      // For F&O - Futures
      const totalMarginPercentage = (spanMargin + exposureMargin + volatilityMargin + adhocMargin + additionalMargin) / 100;
      marginRequired = availableCapital * totalMarginPercentage;
      totalExposure = availableCapital / totalMarginPercentage;
      effectiveLeverage = 1 / totalMarginPercentage;
    } else if (segment === 'options') {
      // For F&O - Options
      if (marginType === 'buy') {
        // Buying options requires premium only
        marginRequired = availableCapital;
        totalExposure = availableCapital; // Premium paid is the total exposure
        effectiveLeverage = 1; // No leverage when buying options
      } else {
        // Selling options requires SPAN + Exposure margin
        const totalMarginPercentage = (spanMargin + exposureMargin + volatilityMargin + adhocMargin + additionalMargin) / 100;
        marginRequired = availableCapital * totalMarginPercentage;
        totalExposure = availableCapital / totalMarginPercentage;
        effectiveLeverage = 1 / totalMarginPercentage;
      }
    }

    // Calculate margin utilization
    marginUtilization = (marginRequired / availableCapital) * 100;
    
    // Calculate maximum number of positions possible
    maxPositionsNumber = Math.floor(totalExposure / (availableCapital / 10)); // Assuming average position size is 10% of capital
    
    // Calculate initial and maintenance margin requirements
    initialMarginRequired = marginRequired;
    maintenanceMarginRequired = initialMarginRequired * (maintenanceMargin / 100);
    
    // Calculate margin call threshold
    marginCallThreshold = availableCapital - (initialMarginRequired - maintenanceMarginRequired);
    
    // Calculate minimum margin that should remain available
    minAvailableMargin = availableCapital - initialMarginRequired;

    return {
      marginRequired,
      totalExposure,
      effectiveLeverage,
      marginUtilization,
      maxPositionsNumber,
      initialMarginRequired,
      maintenanceMarginRequired,
      marginCallThreshold,
      minAvailableMargin
    };
  };

  // Recalculate whenever inputs change
  useEffect(() => {
    setResult(calculateMarginValue());
  }, [
    availableCapital,
    segment,
    marginType,
    leverage,
    exposureMargin,
    spanMargin,
    stockCategory,
    volatilityMargin,
    adhocMargin,
    additionalMargin,
    maintenanceMargin
  ]);

  // Handle share button click
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">Margin Value Calculator</h1>
        <h2 className="text-lg text-gray-600">Calculate trading exposure and margin requirements</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Capital */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Available Capital</h2>
              <div className="grid grid-cols-1 gap-6">
                <div className="col-span-1">
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Available Capital
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('capital')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'capital' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Total funds you have for trading in your account
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={availableCapital}
                    onChange={(e) => setAvailableCapital(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={10000}
                    max={10000000}
                    step={10000}
                    value={availableCapital}
                    onChange={(e) => setAvailableCapital(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹10K</span>
                    <span>₹1Cr</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Segment Type */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Trading Segment</h2>
              <div className="grid grid-cols-1 gap-6">
                <div className="col-span-1">
                  <label className="block font-medium text-sm mb-3 flex items-center">
                    Select Trading Segment
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('segment')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'segment' && (
                        <div className="absolute z-10 left-6 -top-2 w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Different segments have different margin requirements
                        </div>
                      )}
                    </div>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        segment === 'equity-delivery' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSegment('equity-delivery')}
                    >
                      <p className="font-medium">Equity Delivery</p>
                      <p className="text-xs text-gray-500 mt-1">T+2 settlement</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        segment === 'equity-intraday' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSegment('equity-intraday')}
                    >
                      <p className="font-medium">Equity Intraday</p>
                      <p className="text-xs text-gray-500 mt-1">Same-day settlement</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        segment === 'futures' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSegment('futures')}
                    >
                      <p className="font-medium">Futures</p>
                      <p className="text-xs text-gray-500 mt-1">F&O segment</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        segment === 'options' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSegment('options')}
                    >
                      <p className="font-medium">Options</p>
                      <p className="text-xs text-gray-500 mt-1">F&O segment</p>
                    </div>
                  </div>
                </div>

                {segment === 'options' && (
                  <div>
                    <label className="block font-medium text-sm mb-1.5">Options Strategy</label>
                    <div className="grid grid-cols-2 gap-2">
                      <div 
                        className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                          marginType === 'buy' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setMarginType('buy')}
                      >
                        <p className="font-medium">Buy Options</p>
                        <p className="text-xs text-gray-500 mt-1">Premium as margin</p>
                      </div>
                      <div 
                        className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                          marginType === 'sell' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setMarginType('sell')}
                      >
                        <p className="font-medium">Sell Options</p>
                        <p className="text-xs text-gray-500 mt-1">SPAN + Exposure margin</p>
                      </div>
                    </div>
                  </div>
                )}

                {segment === 'equity-delivery' && (
                  <div>
                    <label className="block font-medium text-sm mb-1.5">Trading Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      <div 
                        className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                          marginType === 'delivery' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setMarginType('delivery')}
                      >
                        <p className="font-medium">Delivery</p>
                        <p className="text-xs text-gray-500 mt-1">100% margin</p>
                      </div>
                      <div 
                        className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                          marginType === 'intraday' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setMarginType('intraday')}
                      >
                        <p className="font-medium">Intraday</p>
                        <p className="text-xs text-gray-500 mt-1">Leverage available</p>
                      </div>
                    </div>
                  </div>
                )}

                {segment === 'equity-intraday' && (
                  <div>
                    <label className="block font-medium text-sm mb-1.5">Stock Category</label>
                    <div className="grid grid-cols-3 gap-2">
                      <div 
                        className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                          stockCategory === 'group-a' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setStockCategory('group-a')}
                      >
                        <p className="font-medium">Group A</p>
                        <p className="text-xs text-gray-500 mt-1">20% margin</p>
                      </div>
                      <div 
                        className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                          stockCategory === 'group-b' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setStockCategory('group-b')}
                      >
                        <p className="font-medium">Group B</p>
                        <p className="text-xs text-gray-500 mt-1">30% margin</p>
                      </div>
                      <div 
                        className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                          stockCategory === 'others' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setStockCategory('others')}
                      >
                        <p className="font-medium">Others</p>
                        <p className="text-xs text-gray-500 mt-1">35% margin</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Margin Parameters */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Margin Parameters</h2>
              <div className="grid grid-cols-2 gap-6">
                {(segment === 'equity-delivery' && marginType === 'intraday') && (
                  <div>
                    <label className="block font-medium text-sm mb-1.5 flex items-center">
                      Leverage
                      <div className="relative ml-1">
                        <HelpCircle 
                          size={14} 
                          className="text-gray-400 cursor-pointer"
                          onMouseEnter={() => setShowToolTip('leverage')}
                          onMouseLeave={() => setShowToolTip('')}
                        />
                        {showToolTip === 'leverage' && (
                          <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                            Multiplier applied to your capital for intraday trading
                          </div>
                        )}
                      </div>
                    </label>
                    <input
                      type="number"
                      value={leverage}
                      onChange={(e) => setLeverage(Number(e.target.value))}
                      className="w-full p-1.5 border rounded text-sm"
                    />
                    <input
                      type="range"
                      min={1}
                      max={20}
                      step={1}
                      value={leverage}
                      onChange={(e) => setLeverage(Number(e.target.value))}
                      className="w-full mt-2"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1x</span>
                      <span>20x</span>
                    </div>
                  </div>
                )}

                {(segment === 'futures' || (segment === 'options' && marginType === 'sell')) && (
                  <>
                    <div>
                      <label className="block font-medium text-sm mb-1.5">SPAN Margin (%)</label>
                      <input
                        type="number"
                        value={spanMargin}
                        onChange={(e) => setSpanMargin(Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={5}
                        max={50}
                        step={1}
                        value={spanMargin}
                        onChange={(e) => setSpanMargin(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>5%</span>
                        <span>50%</span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5">Exposure Margin (%)</label>
                      <input
                        type="number"
                        value={exposureMargin}
                        onChange={(e) => setExposureMargin(Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={1}
                        max={10}
                        step={0.5}
                        value={exposureMargin}
                        onChange={(e) => setExposureMargin(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>1%</span>
                        <span>10%</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Advanced Settings</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    {(segment === 'futures' || (segment === 'options' && marginType === 'sell')) && (
                      <>
                        <div>
                          <label className="block font-medium text-sm mb-1.5">Volatility Margin (%)</label>
                          <input
                            type="number"
                            value={volatilityMargin}
                            onChange={(e) => setVolatilityMargin(Number(e.target.value))}
                            className="w-full p-1.5 border rounded text-sm"
                          />
                          <input
                            type="range"
                            min={0}
                            max={15}
                            step={0.5}
                            value={volatilityMargin}
                            onChange={(e) => setVolatilityMargin(Number(e.target.value))}
                            className="w-full mt-2"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>0%</span>
                            <span>15%</span>
                          </div>
                        </div>

                        <div>
                          <label className="block font-medium text-sm mb-1.5">Ad-hoc Margin (%)</label>
                          <input
                            type="number"
                            value={adhocMargin}
                            onChange={(e) => setAdhocMargin(Number(e.target.value))}
                            className="w-full p-1.5 border rounded text-sm"
                          />
                          <input
                            type="range"
                            min={0}
                            max={20}
                            step={0.5}
                            value={adhocMargin}
                            onChange={(e) => setAdhocMargin(Number(e.target.value))}
                            className="w-full mt-2"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>0%</span>
                            <span>20%</span>
                          </div>
                        </div>

                        <div>
                          <label className="block font-medium text-sm mb-1.5">Additional Margin (%)</label>
                          <input
                            type="number"
                            value={additionalMargin}
                            onChange={(e) => setAdditionalMargin(Number(e.target.value))}
                            className="w-full p-1.5 border rounded text-sm"
                          />
                          <input
                            type="range"
                            min={0}
                            max={20}
                            step={0.5}
                            value={additionalMargin}
                            onChange={(e) => setAdditionalMargin(Number(e.target.value))}
                            className="w-full mt-2"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>0%</span>
                            <span>20%</span>
                          </div>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block font-medium text-sm mb-1.5 flex items-center">
                        Maintenance Margin (%)
                        <div className="relative ml-1">
                          <HelpCircle 
                            size={14} 
                            className="text-gray-400 cursor-pointer"
                            onMouseEnter={() => setShowToolTip('maintenance')}
                            onMouseLeave={() => setShowToolTip('')}
                          />
                          {showToolTip === 'maintenance' && (
                            <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                              Minimum margin that must be maintained to avoid a margin call
                            </div>
                          )}
                        </div>
                      </label>
                      <input
                        type="number"
                        value={maintenanceMargin}
                        onChange={(e) => setMaintenanceMargin(Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={10}
                        max={100}
                        step={1}
                        value={maintenanceMargin}
                        onChange={(e) => setMaintenanceMargin(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>10%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[500px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Margin Analysis</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(result.totalExposure)}
                  </div>
                  <div className="text-sm text-gray-300">Total Exposure Value</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Margin Required</span>
                    <span className="font-bold">{formatCurrency(result.marginRequired)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Effective Leverage</span>
                    <span className="font-bold">{result.effectiveLeverage.toFixed(2)}x</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Margin Utilization</span>
                    <span className="font-bold">{result.marginUtilization.toFixed(2)}%</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Maximum Positions</span>
                    <span className="font-bold">{result.maxPositionsNumber}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Maintenance Margin</span>
                    <span className="font-bold">{formatCurrency(result.maintenanceMarginRequired)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Margin Call Threshold</span>
                    <span className="font-bold">{formatCurrency(result.marginCallThreshold)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Available Margin</span>
                    <span className="font-bold">{formatCurrency(result.minAvailableMargin)}</span>
                  </div>
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Open Trading Account →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarginCalculator;